import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { ClipLoader } from 'react-spinners';
import { useRemoveAuth } from 'hooks/useRemoveAuth';
import { Logout } from '@mui/icons-material';
import LoginBg from 'assets/bg/login_bg.svg';
import { SVG } from 'components/Asset/SVG';
import { useTranslation } from 'react-i18next';

const LoggingOutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  background-image: url(${LoginBg});
  background-repeat: no-repeat;
  background-size: cover;
  color: white;
`;

const LogoContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoutIcon = styled(Logout)`
  font-size: 48px !important;
  margin-bottom: 16px;
  color: #3f51b5;
`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 32px;
  font-weight: 500;
`;

const Message = styled.p`
  font-size: 16px;
  margin-bottom: 32px;
  opacity: 0.8;
`;

/**
 * LoggingOutScreen component
 * Shows a loading animation while performing logout operations
 * and then redirects to login page
 */
const LoggingOutScreen: React.FC = () => {
  const { t } = useTranslation();
  const { execRemoveAuth } = useRemoveAuth();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  useEffect(() => {
    // Clear session data and perform logout operations
    const performLogout = () => {
      // Execute authentication removal
      execRemoveAuth();
      // Wait for 2 seconds to show the animation before redirecting
      setTimeout(() => {
        setIsRedirecting(true);
      }, 5000);
    };
    performLogout();
  }, [execRemoveAuth]);

  if (isRedirecting) {
    return <Navigate to="/login" />;
  }

  return (
    <LoggingOutContainer>
      <LogoContainer
        style={{
          backgroundColor: 'white',
          width: '300px',
          height: '150px',
          borderRadius: '8px',
        }}
      >
        <SVG name="symboticwareLogoFullDirectionColumn" width={290} />
      </LogoContainer>
      <Title>{t('logging_out')}</Title>
      <Message>{t('please_wait_while_we_securely_sign_you_out')}</Message>
      <ClipLoader color="#3f51b5" size={50} />
    </LoggingOutContainer>
  );
};

export default LoggingOutScreen;
